import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Modal, Radio, Row, Spin, Typography, message } from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  GiftOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import AuthBrandPanel from '../components/auth/AuthBrandPanel';
import { AUTH_PAGE_STYLES } from '../components/auth/authPageStyles';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { authAPI, cmsAPI } from '../services/api';
import { markdownToHtml } from '../utils/markdown';
import { safeErrorMessage } from '../utils/errorHandler';

const { Title, Text } = Typography;

type AgreementDocSlug = 'terms' | 'privacy';

const AGREEMENT_DOC_LABELS: Record<AgreementDocSlug, string> = {
  terms: '服务条款',
  privacy: '隐私政策',
};

type AgreementDoc = {
  title: string;
  content: string;
};

interface TenantSearchItem {
  tenant_id: string;
  name: string;
  tenant_code: string;
  exact_match: boolean;
}

const Register: React.FC = () => {
  const { register } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [activeAgreementDoc, setActiveAgreementDoc] = useState<AgreementDocSlug>('terms');
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState<string | null>(null);
  const [hasViewedTerms, setHasViewedTerms] = useState(false);
  const [hasViewedPrivacy, setHasViewedPrivacy] = useState(false);
  const [agreementDocs, setAgreementDocs] = useState<Record<AgreementDocSlug, AgreementDoc | null>>({
    terms: null,
    privacy: null,
  });
  const [tenantMatches, setTenantMatches] = useState<TenantSearchItem[]>([]);
  const [tenantSearching, setTenantSearching] = useState(false);
  const { slogan, siteName, userLoginLogoUrl } = useSiteBranding();
  const acceptedTerms = Form.useWatch('acceptTerms', form);
  const acceptedPrivacy = Form.useWatch('acceptPrivacy', form);
  const organizationName = Form.useWatch('organization_name', form) || '';
  const selectedTenantId = Form.useWatch('tenant_id', form);
  const createNewTenant = Form.useWatch('create_new_tenant', form);

  useEffect(() => {
    setLogoFailed(false);
  }, [userLoginLogoUrl]);

  useEffect(() => {
    const query = organizationName.trim();
    form.setFieldsValue({ tenant_id: undefined, create_new_tenant: false });
    if (query.length < 2) {
      setTenantMatches([]);
      setTenantSearching(false);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setTenantSearching(true);
      try {
        const result = await authAPI.searchOrganizations(query);
        if (active) setTenantMatches(result.items || []);
      } catch {
        if (active) setTenantMatches([]);
      } finally {
        if (active) setTenantSearching(false);
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [form, organizationName]);

  const openAgreementDocument = async (slug: AgreementDocSlug) => {
    setActiveAgreementDoc(slug);
    setAgreementModalOpen(true);
    setAgreementError(null);

    if (slug === 'terms') {
      setHasViewedTerms(true);
    }
    if (slug === 'privacy') {
      setHasViewedPrivacy(true);
    }

    if (agreementDocs[slug]) {
      return;
    }

    setAgreementLoading(true);
    try {
      const page = await cmsAPI.getPage(slug);
      setAgreementDocs((prev) => ({
        ...prev,
        [slug]: {
          title: page?.title || AGREEMENT_DOC_LABELS[slug],
          content: page?.content || '',
        },
      }));
    } catch (err: any) {
      setAgreementError(err?.response?.data?.detail || '协议内容加载失败，请稍后重试。');
    } finally {
      setAgreementLoading(false);
    }
  };

  const onFinish = async (values: {
    email: string;
    password: string;
    confirm?: string;
    username?: string;
    real_name: string;
    phone: string;
    organization_name: string;
    tenant_id?: string;
    create_new_tenant?: boolean;
    invite_code?: string;
    tenant_invitation_token?: string;
    acceptTerms?: boolean;
    acceptPrivacy?: boolean;
  }) => {
    if (!hasViewedTerms || !hasViewedPrivacy) {
      message.warning('请先浏览服务条款和隐私政策。');
      return;
    }

    const exactMatches = tenantMatches.filter((item) => item.exact_match);
    if (exactMatches.length > 0 && !values.tenant_id && !values.create_new_tenant) {
      message.warning(exactMatches.length > 1 ? '存在多个同名机构，请选择正确的机构记录。' : '该机构已存在，请选择已有机构或明确创建新机构。');
      return;
    }

    setLoading(true);
    try {
      const {
        acceptTerms: _acceptTerms,
        acceptPrivacy: _acceptPrivacy,
        confirm: _confirm,
        ...registerPayload
      } = values;
      const result = await register({
        ...registerPayload,
        tenant_invitation_token: searchParams.get('tenant_invitation') || undefined,
      });
      message.success(result.message || '注册成功，请查收邮箱验证码');
      navigate(`/verify-email?email=${encodeURIComponent(values.email)}`, { replace: true });
    } catch (err: any) {
      message.error(safeErrorMessage(err, '注册失败'));
    } finally {
      setLoading(false);
    }
  };

  const activeAgreement = agreementDocs[activeAgreementDoc];
  const canSubmit = Boolean(acceptedTerms && acceptedPrivacy && hasViewedTerms && hasViewedPrivacy);

  return (
    <div className={`auth-page auth-page--register ${mode === 'dark' ? 'is-dark' : ''}`}>
      <style>{AUTH_PAGE_STYLES}</style>

      <AuthBrandPanel
        description={slogan}
        siteName={siteName}
        logoUrl={userLoginLogoUrl}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <div className="auth-page-right">
        <div className="auth-shell auth-shell-wide auth-shell-register">
          <Card className="auth-card auth-card--register" bordered={false}>
            <div className="auth-topbar">
              <div className="auth-badge">
                <SafetyCertificateOutlined />
                <span>安全账户注册</span>
              </div>
              <Link to="/" className="auth-back-link">
                <ArrowLeftOutlined style={{ marginRight: 6 }} />
                返回首页
              </Link>
            </div>

            <div className="auth-header">
              <Title className="auth-title">注册登记</Title>

            </div>

            <Form form={form} onFinish={onFinish} size="large" className="auth-form" layout="vertical">
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name="real_name" rules={[{ required: true, message: '请输入真实姓名' }]}>
                    <Input className="auth-input auth-input--compact" prefix={<IdcardOutlined />} placeholder="真实姓名" autoComplete="name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="username">
                    <Input
                      className="auth-input auth-input--compact"
                      prefix={<UserOutlined />}
                      placeholder="用户名"
                      autoComplete="username"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    rules={[
                      { required: true, message: '请输入手机号' },
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入有效手机号' },
                    ]}
                  >
                    <Input className="auth-input auth-input--compact" prefix={<PhoneOutlined />} placeholder="手机号" autoComplete="tel" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: '请输入邮箱' },
                      { type: 'email', message: '请输入有效邮箱' },
                    ]}
                  >
                    <Input className="auth-input auth-input--compact" prefix={<MailOutlined />} placeholder="邮箱" autoComplete="email" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="organization_name" rules={[{ required: true, message: '请输入机构名称' }]}>
                    <Input
                      className="auth-input auth-input--compact"
                      prefix={<BankOutlined />}
                      placeholder="机构名称（将作为您的工作区名称）"
                      autoComplete="organization"
                    />
                  </Form.Item>
                  {(tenantSearching || tenantMatches.length > 0) && (
                    <div style={{ marginTop: -12, marginBottom: 16, padding: 12, border: '1px solid #D9E2EC', background: '#F8FAFC' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 13 }}>历史机构记录</Text>
                        {tenantSearching && <Spin size="small" />}
                      </div>
                      {tenantMatches.length > 0 && (
                        <Form.Item name="tenant_id" style={{ marginBottom: 8 }}>
                          <Radio.Group
                            style={{ width: '100%' }}
                            onChange={() => form.setFieldValue('create_new_tenant', false)}
                          >
                            <div style={{ display: 'grid', gap: 8 }}>
                              {tenantMatches.map((tenant) => (
                                <label
                                  key={tenant.tenant_id}
                                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer' }}
                                >
                                  <Radio value={tenant.tenant_id} style={{ marginTop: 2 }} />
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                      <Text strong>{tenant.name}</Text>
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </Radio.Group>
                        </Form.Item>
                      )}
                      <Form.Item name="create_new_tenant" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox
                          onChange={(event) => {
                            if (event.target.checked) form.setFieldValue('tenant_id', undefined);
                          }}
                        >
                          我不隶属于该机构，创建新的“{organizationName.trim()}”机构
                        </Checkbox>
                      </Form.Item>
                      {selectedTenantId && !createNewTenant && (
                        <Alert type="success" showIcon style={{ marginTop: 8 }} message="注册后将作为成员加入所选机构，机构原管理员保持不变。" />
                      )}
                    </div>
                  )}
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: '请输入密码' },
                      { min: 6, message: '密码至少 6 位' },
                    ]}
                  >
                    <Input.Password className="auth-input auth-input--compact" prefix={<LockOutlined />} placeholder="密码" autoComplete="new-password" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="confirm"
                    dependencies={['password']}
                    rules={[
                      { required: true, message: '请确认密码' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次密码输入不一致'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password className="auth-input auth-input--compact" prefix={<LockOutlined />} placeholder="确认密码" autoComplete="new-password" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="invite_code" initialValue={searchParams.get('invite') || undefined}>
                    <Input className="auth-input auth-input--compact" prefix={<GiftOutlined />} placeholder="邀请码（选填，注册可获金币）" />
                  </Form.Item>
                </Col>
              </Row>

              <div className="auth-consent-wrap">
                <div className="auth-consent-tip">点击阅读《服务条款》、《隐私政策》，再勾选。</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <Form.Item name="acceptTerms" valuePropName="checked" noStyle>
                    <Checkbox disabled={!hasViewedTerms} />
                  </Form.Item>
                  <div style={{ lineHeight: 1.6 }}>
                    <span style={{ color: '#5A6577' }}>我已阅读并同意</span>
                    <button
                      type="button"
                      onClick={() => openAgreementDocument('terms')}
                      style={{
                        marginLeft: 4,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        color: '#D9363E',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                    >
                      《服务条款》
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Form.Item name="acceptPrivacy" valuePropName="checked" noStyle>
                    <Checkbox disabled={!hasViewedPrivacy} />
                  </Form.Item>
                  <div style={{ lineHeight: 1.6 }}>
                    <span style={{ color: '#5A6577' }}>我已阅读并同意</span>
                    <button
                      type="button"
                      onClick={() => openAgreementDocument('privacy')}
                      style={{
                        marginLeft: 4,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        color: '#D9363E',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                    >
                      《隐私政策》
                    </button>
                    <span style={{ color: '#5A6577' }}>，并知悉上述免责声明。</span>
                  </div>
                </div>
              </div>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button className="auth-submit" type="primary" htmlType="submit" loading={loading} disabled={!canSubmit} block>
                  注册
                </Button>
              </Form.Item>
            </Form>

            <Divider className="auth-divider" plain>
              <Text type="secondary">已有账号？</Text>
            </Divider>
            <div className="auth-link-copy">
              <Link to="/login" className="auth-link-accent">
                返回登录
              </Link>
            </div>
          </Card>
        </div>
      </div>

      <Modal
        title={activeAgreement?.title || AGREEMENT_DOC_LABELS[activeAgreementDoc]}
        open={agreementModalOpen}
        onCancel={() => setAgreementModalOpen(false)}
        footer={
          <Button type="primary" onClick={() => setAgreementModalOpen(false)}>
            已阅读，返回注册
          </Button>
        }
        width={860}
      >
        {agreementLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '56px 0' }}>
            <Spin size="large" />
          </div>
        ) : agreementError ? (
          <Alert type="error" showIcon message="协议内容加载失败" description={agreementError} />
        ) : (
          <div
            className="content-page-content"
            style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
            dangerouslySetInnerHTML={{ __html: markdownToHtml(activeAgreement?.content || '') }}
          />
        )}
      </Modal>
    </div>
  );
};

export default Register;
